import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import {
  createFundBasket,
  deleteFundBasket,
  getBacktestedFundBasketData,
  getFundBasket,
  getFundBasketAnalytics,
  getFundBasketChart,
  getFundBasketClients,
  getFundBaskets,
  notifyFundBasketRebalance,
  updateFundBasket,
  type ListFundBasketsParams,
} from "../../api/funds/basket";
import {
  CustomQueryOptions,
  FundBasketChartData,
  FundBasketCreateUpdate,
} from "../../types";
import { fundBasketRebalanceKeys } from "./rebalance";
import { fundBasketKeys } from "./common";

export const fundBasketsQueryOptions = (
  variables?: ListFundBasketsParams,
  options?: CustomQueryOptions<typeof getFundBaskets>
) =>
  queryOptions({
    queryKey: fundBasketKeys.list(variables),
    queryFn: () => getFundBaskets(variables),
    placeholderData: (prev) => prev,
    ...options,
  });

export function useFundBaskets(
  ...params: Parameters<typeof fundBasketsQueryOptions>
) {
  return useQuery(fundBasketsQueryOptions(...params));
}

export const fundBasketQueryOptions = (
  id: number,
  options?: CustomQueryOptions<typeof getFundBasket>
) =>
  queryOptions({
    queryKey: fundBasketKeys.detail(id),
    queryFn: () => getFundBasket(id),
    placeholderData: (prev) => prev,
    staleTime: 5 * 60 * 1000,
    ...options,
    enabled: options?.enabled ?? !!id,
  });

export function useFundBasket(
  id: number,
  options?: CustomQueryOptions<typeof getFundBasket>
) {
  return useQuery(fundBasketQueryOptions(id, options));
}

export const fundBasketAnalyticsQueryOptions = (
  variables: { id: number; benchmark?: string },
  options?: CustomQueryOptions<typeof getFundBasketAnalytics>
) =>
  queryOptions({
    queryKey: fundBasketKeys.analytics.detail(
      variables.id,
      variables.benchmark
    ),
    queryFn: () => getFundBasketAnalytics(variables.id, variables.benchmark),
    staleTime: 5 * 60 * 1000,
    ...options,
    enabled: options?.enabled ?? !!variables.id,
  });

export function useFundBasketAnalytics(
  ...params: Parameters<typeof fundBasketAnalyticsQueryOptions>
) {
  return useQuery(fundBasketAnalyticsQueryOptions(...params));
}

export const fundBasketChartQueryOptions = (
  variables: Parameters<typeof getFundBasketChart>[0],
  options?: { enabled?: boolean }
) =>
  queryOptions({
    queryKey: fundBasketKeys.chart.detail(
      variables.id,
      variables.period,
      variables.benchmark
    ),
    queryFn: () => getFundBasketChart(variables),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export function useFundBasketChart(
  variables: Parameters<typeof getFundBasketChart>[0],
  options?: { enabled?: boolean; initialData?: FundBasketChartData }
) {
  return useQuery(fundBasketChartQueryOptions(variables, options));
}

export const backtestedFundBasketDataQueryOptions = (
  variables: Parameters<typeof getBacktestedFundBasketData>[0],
  options?: { enabled?: boolean }
) =>
  queryOptions({
    queryKey: fundBasketKeys.chart.backtest(variables.id, {
      period: variables.period,
      benchmark: variables.benchmark,
      initialAmount: variables.initialAmount,
      sipAmount: variables.sipAmount,
    }),
    queryFn: () => getBacktestedFundBasketData(variables),
    ...options,
    enabled: options?.enabled ?? !!variables.id,
  });

export function useBacktestedFundBasketData(
  variables: Parameters<typeof getBacktestedFundBasketData>[0],
  options?: { enabled?: boolean }
) {
  return useQuery(backtestedFundBasketDataQueryOptions(variables, options));
}

export function useCreateFundBasket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createFundBasket,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fundBasketKeys.all });
    },
  });
}

export function useUpdateFundBasket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FundBasketCreateUpdate & { id: number }) =>
      updateFundBasket(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: fundBasketKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: fundBasketKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: fundBasketKeys.analytics.all(),
      });
      queryClient.invalidateQueries({
        queryKey: fundBasketKeys.chart.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: fundBasketRebalanceKeys.linkedSips(variables.id),
      });
    },
  });
}

export function useDeleteFundBasket(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...fundBasketKeys.detail(id), "delete"],
    mutationFn: () => deleteFundBasket(id),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: fundBasketKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: fundBasketKeys.lists() });
      queryClient.removeQueries({
        queryKey: fundBasketKeys.analytics.detail(id),
      });
      queryClient.removeQueries({ queryKey: fundBasketKeys.chart.detail(id) });
    },
  });
}

export function useFetchFundBasketById() {
  const querClient = useQueryClient();
  return useCallback(
    async (id: number) => {
      const data = await querClient.ensureQueryData(fundBasketQueryOptions(id));
      return data;
    },
    [querClient]
  );
}

export const fundBasketClientsQueryOptions = (basketId?: number) =>
  queryOptions({
    queryKey: fundBasketKeys.clients(basketId),
    queryFn: () => getFundBasketClients(basketId),
    select(data) {
      return data?.[0];
    },
    staleTime: 5 * 60 * 1000,
  });

export function useFundBasketClients(basketId?: number) {
  return useQuery(fundBasketClientsQueryOptions(basketId));
}

export function useNotifyFundBasketRebalance(id: number) {
  return useMutation({
    mutationFn: () => notifyFundBasketRebalance(id),
  });
}
